import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import { openFeedbackDialog, openPreconditionsDialog } from '@/features/app/MessageHandler';
import TimestampAndMileage from '@/features/engineoil/components/TimestampAndMileage';
import {
    ENGINE_OIL_STATE,
    ENGINE_OIL_WORKSHOP_ACTIONS_TI,
    ENGINE_OIL_WORKSHOP_ACTIONS_TI_LINK,
    SOURCE_DEVICE,
} from '@/features/engineoil/utils/constants';
import { getBarColorClass, getThresholdClass, isEngineOilOutdated } from '@/features/engineoil/utils/utils';
import type { EngineOil } from '@/generated/GeneratedApiTypes';
import useHoverIntent from '@/hooks/useHoverIntent';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import BasePanel from './BasePanel';

interface Tbm3DidCardProps {
    engineOil: EngineOil;
}

// Tbm3DidCard = TBM3 DID
const Tbm3DidCard = ({ engineOil }: Tbm3DidCardProps) => {
    const intl = useIntl();
    const { asset_id, state, status, measurement, measured_at } = engineOil;
    const [hoverRef] = useHoverIntent(1000, () => AnalyticsEvents.hoverDidCardOlder24h(SOURCE_DEVICE.TBM3));

    const isReadingNotPossible = isEngineOilOutdated(measured_at.date);
    const stateKey = isReadingNotPossible ? 'reading_not_possible' : state;
    const hasRecommendation = state !== ENGINE_OIL_STATE.OK || isReadingNotPossible;

    const thresholdClass = getThresholdClass(status, isReadingNotPossible);
    const barColorClass = getBarColorClass(status, isReadingNotPossible);
    const textColorClass = isReadingNotPossible ? 'text-color-gray' : 'text-color-gray-darkest';
    const percentage = measurement != null ? Math.round(measurement) : 0;

    return (
        <BasePanel thresholdClass={thresholdClass} testId="card-workshop-tbm3-did">
            <div className="padding-10 text-size-12 right-0 top-0 margin-1 position-absolute">
                <a
                    href={ENGINE_OIL_WORKSHOP_ACTIONS_TI_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover-text-decoration-none"
                >
                    {ENGINE_OIL_WORKSHOP_ACTIONS_TI}
                </a>
            </div>

            <div className="display-flex flex-wrap">
                {isReadingNotPossible && (
                    <div className="text-size-14 text-color-dark">
                        <FormattedMessage id="intl-msg:engineOil.latestValidReading" />
                        &nbsp;
                    </div>
                )}
                <TimestampAndMileage timestamp={measured_at.date} mileage={measured_at.mileage_km} />
            </div>

            <div className="width-100pct display-flex align-items-center padding-top-10">
                <div className="progress margin-0 flex-1-0" style={{ height: '10px' }}>
                    <div className={`progress-bar ${barColorClass}`.trim()} style={{ width: `${percentage}%` }} />
                </div>
                <div className={`margin-left-10 ${textColorClass}`}>{`${percentage} %`}</div>
                {isReadingNotPossible && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                <FormattedMessage id="intl-msg:engineOil.olderThan24h" />
                            </Tooltip>
                        }
                    >
                        <span
                            ref={hoverRef}
                            className={
                                'rioglyph rioglyph-exclamation-sign text-color-dark ' +
                                'text-size-16 width-auto margin-top-0 margin-left-5'
                            }
                        />
                    </OverlayTrigger>
                )}
            </div>

            <div className="text-size-14">
                <FormattedMessage id={`intl-msg:engineOil.state.${stateKey}`} />
            </div>

            {hasRecommendation && (
                <div className="text-size-12 text-color-dark">
                    <FormattedMessage id={`intl-msg:engineOil.card.tbm3Did.recommendation.${stateKey}`} />
                </div>
            )}

            <div className="btn-toolbar margin-top-15 display-flex" style={{ justifyContent: 'space-between' }}>
                <Button
                    iconName="rioglyph-detail-view-info"
                    bsSize={Button.SM}
                    onClick={() => {
                        AnalyticsEvents.openDidPreconditionsDialog(SOURCE_DEVICE.TBM3);
                        openPreconditionsDialog(
                            asset_id,
                            intl.formatMessage({ id: 'intl-msg:engineOil.preconditions.dialog.title' }),
                        );
                    }}
                >
                    <FormattedMessage id="intl-msg:engineOil.preconditionsButton" />
                </Button>
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip>
                            <FormattedMessage id="intl-msg:engineOil.tooltip.rateYourExperience" />
                        </Tooltip>
                    }
                >
                    <Button
                        iconOnly
                        bsSize={Button.SM}
                        onClick={() => {
                            AnalyticsEvents.openDidFeedbackEngineOil(SOURCE_DEVICE.TBM3);
                            openFeedbackDialog();
                        }}
                    >
                        <span className="rioglyph rioglyph-thumbs-up" />
                    </Button>
                </OverlayTrigger>
            </div>
        </BasePanel>
    );
};

export default Tbm3DidCard;
